/** React glue for the overlay store: context + per-page subscriptions (objects and UI slice). */
import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import type { AnnotationObject } from '../../shared/annotations';
import type { PageSize } from '../../shared/dto/library';
import type { OverlayStore, PageUi } from './store';

export interface AnnotateCtx {
  store: OverlayStore;
  pages: readonly PageSize[];
}

export const AnnotateContext = createContext<AnnotateCtx | null>(null);

export function useAnnotateCtx(): AnnotateCtx {
  const c = useContext(AnnotateContext);
  if (!c) throw new Error('AnnotateContext missing');
  return c;
}

export function usePageObjects(store: OverlayStore, page: number): readonly AnnotationObject[] {
  const subscribe = useCallback((cb: () => void) => store.subscribePage(page, cb), [store, page]);
  const get = useCallback(() => store.getPage(page), [store, page]);
  return useSyncExternalStore(subscribe, get, get);
}

/** The overlay UI as seen by one page (re-renders only when something on that page changes). */
export function usePageUi(store: OverlayStore, page: number): PageUi {
  const get = useCallback(() => store.getPageUi(page), [store, page]);
  return useSyncExternalStore(store.subscribeUi, get, get);
}
